import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { getTrip, uploadDocument, deleteDocument, generateRecommendations, getDocumentFile } from '../api/trips';
import { format, parseISO, eachDayOfInterval } from 'date-fns';
import './TripDetailPage.css';

// ─── Event type config ───────────────────────────────────────
const EVENT_TYPE = {
  flight:     { icon: '✈️', color: '#3b82f6', label: 'Flight' },
  hotel:      { icon: '🏨', color: '#8b5cf6', label: 'Hotel' },
  car_rental: { icon: '🚗', color: '#10b981', label: 'Car Rental' },
  transfer:   { icon: '🚕', color: '#f59e0b', label: 'Transfer' },
  activity:   { icon: '🎯', color: '#ef4444', label: 'Activity' },
  other:      { icon: '📋', color: '#6b7280', label: 'Other' },
};

// ─── Recommendation type config ──────────────────────────────
const REC_TYPE = {
  restaurant: '🍽️',
  tour:       '🗺️',
  attraction: '🏛️',
  tip:        '💡',
  activity:   '🎭',
};

// ─── Event Detail Card ───────────────────────────────────────
function EventDetails({ event, onViewDocument }) {
  const d = event.details || {};
  const cfg = EVENT_TYPE[event.event_type] || EVENT_TYPE.other;
  const linkedDocs = event.linked_documents || [];

  const rows = [
    d.time && { label: 'Time', value: d.time },
    d.booking_reference && { label: 'Booking Ref', value: d.booking_reference },
    d.flight_number && { label: 'Flight', value: d.flight_number },
    d.airline && { label: 'Airline', value: d.airline },
    d.seat && { label: 'Seat', value: d.seat },
    d.terminal && { label: 'Terminal', value: d.terminal },
    d.hotel_name && { label: 'Hotel', value: d.hotel_name },
    d.room_type && { label: 'Room', value: d.room_type },
    d.checkout && { label: 'Check-out', value: (() => {
      try {
        const dObj = new Date(d.checkout);
        return isNaN(dObj.getTime()) ? d.checkout : format(dObj, 'MMM d, yyyy');
      } catch (e) {
        return d.checkout;
      }
    })() },
    d.car_company && { label: 'Company', value: d.car_company },
    d.car_type && { label: 'Car', value: d.car_type },
    d.pickup_location && { label: 'Pickup', value: d.pickup_location },
    d.dropoff_location && { label: 'Dropoff', value: d.dropoff_location },
    d.driver_name && { label: 'Driver', value: d.driver_name },
    d.price && { label: 'Price', value: d.price },
    d.notes && { label: 'Notes', value: d.notes },
  ].filter(Boolean);

  return (
    <div className="event-details">
      <div className="event-badge" style={{ background: cfg.color + '18', color: cfg.color }}>
        <span>{cfg.icon}</span>
        <span>{cfg.label}</span>
      </div>

      {rows.length > 0 && (
        <div className="detail-grid">
          {rows.map(({ label, value }) => (
            <div key={label} className="detail-row">
              <span className="detail-label">{label}</span>
              <span className="detail-value">{value}</span>
            </div>
          ))}
        </div>
      )}

      {d.locations?.from && d.locations?.to && (
        <div className="detail-route">
          <span>{d.locations.from}</span>
          <span className="route-arrow">→</span>
          <span>{d.locations.to}</span>
        </div>
      )}

      {/* Render linked document links with passenger names */}
      {linkedDocs.length > 0 ? (
        <div className="detail-links-row">
          {linkedDocs.map((doc, i) => (
            <span key={i} className="detail-link-item">
              {doc.document_id ? (
                <button 
                  onClick={() => onViewDocument({ id: doc.document_id, name: `${cfg.label} Confirmation` })}
                  className="detail-link"
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit' }}
                >
                  View confirmation{doc.passenger_name ? ` → ${doc.passenger_name}` : ''} ↗
                </button>
              ) : doc.confirmation_link ? (
                <a href={doc.confirmation_link} target="_blank" rel="noopener noreferrer" className="detail-link">
                  View confirmation{doc.passenger_name ? ` → ${doc.passenger_name}` : ''} ↗
                </a>
              ) : (
                <span className="detail-link-label">
                  📎 {doc.passenger_name || 'Document'}
                </span>
              )}
              {i < linkedDocs.length - 1 && <span className="detail-link-sep">|</span>}
            </span>
          ))}
        </div>
      ) : d.confirmation_link ? (
        <a href={d.confirmation_link} target="_blank" rel="noopener noreferrer" className="detail-link">
          View confirmation ↗
        </a>
      ) : null}
    </div>
  );
}

// ─── Recommendations Panel ───────────────────────────────────
function RecommendationsPanel({ tripId, date, existingRec }) {
  const [rec, setRec] = useState(existingRec || null);
  const [loading, setLoading] = useState(false);

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const result = await generateRecommendations(tripId, date);
      setRec(result);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const items = rec?.items || [];

  return (
    <div className="recs-panel">
      <div className="recs-header">
        <span className="recs-title">✨ AI Recommendations</span>
        <button
          className="btn-gen-recs"
          onClick={handleGenerate}
          disabled={loading}
        >
          {loading ? (
            <><span className="spinner-sm" /> Generating...</>
          ) : rec ? (
            '↺ Regenerate'
          ) : (
            '⚡ Generate'
          )}
        </button>
      </div>

      {items.length > 0 ? (
        <div className="recs-list">
          {items.map((item, i) => (
            <div key={i} className="rec-item">
              <span className="rec-emoji">{item.emoji || REC_TYPE[item.type] || '📌'}</span>
              <div className="rec-content">
                <div className="rec-name">{item.title}</div>
                <div className="rec-desc">{item.description}</div>
              </div>
            </div>
          ))}
        </div>
      ) : !loading ? (
        <p className="recs-empty">
          Generate AI-powered recommendations for restaurants, tours, and local tips.
        </p>
      ) : null}
    </div>
  );
}

// ─── Day Location Title Helper ───────────────────────────────
// Priority: 1) Flight  2) Hotel check-in  3) null (inherited by caller)
// Transfers/taxis are NEVER used for the day title.
function getDayLocationTitle(events, previousCity) {
  if (!events || events.length === 0) return null;

  const flightEvent = events.find(e => e.event_type === 'flight');
  const hotelEvent = events.find(e => e.event_type === 'hotel');

  let sleepCity = null;
  if (hotelEvent) {
    const locs = (hotelEvent.details || {}).locations || {};
    sleepCity = locs.city || locs.city_to || hotelEvent.details?.hotel_name;
  }

  // ── Priority 1: Flight ───────────────────────────────────────
  if (flightEvent) {
    const locs = (flightEvent.details || {}).locations || {};
    const from = locs.city_from;
    const to   = locs.city_to;
    // Prefer sleep city if available, otherwise fallback to flight destination
    const finalDest = sleepCity || to || from; 

    if (from && to && from !== to) {
      return { text: `${from} → ${to}`, icon: '✈️', destCity: finalDest };
    }
    if (finalDest) return { text: finalDest, icon: '📍', destCity: finalDest };
  }

  // ── Priority 2: Hotel check-in ──────────────────────────────
  if (sleepCity) {
    if (previousCity && previousCity !== sleepCity) {
      // Checking into a new city creates a car travel day!
      return { text: `${previousCity} → ${sleepCity}`, icon: '🚗', destCity: sleepCity };
    }
    return { text: sleepCity, icon: '📍', destCity: sleepCity };
  }

  // ── Priority 3: Nothing usable — caller will inherit ────────
  return null;
}


// ─── Day Accordion ───────────────────────────────────────────
function DayAccordion({ date, events, recommendation, tripId, isToday, locationTitle, unplanned, onViewDocument }) {
  const [open, setOpen] = useState(isToday);

  const dateLabel = format(parseISO(date), 'EEEE, MMMM d');

  return (
    <div className={`day-accordion ${open ? 'open' : ''} ${isToday ? 'today' : ''} ${unplanned ? 'unplanned' : ''}`}>
      <button className="day-header" onClick={() => setOpen(!open)}>
        <div className="day-header-left">
          <div className="day-date-block">
            <span className="day-num">{format(parseISO(date), 'd')}</span>
            <span className="day-month">{format(parseISO(date), 'MMM')}</span>
          </div>
          <div className="day-info">
            <span className="day-label">{dateLabel}</span>
            {isToday && <span className="today-badge">Today</span>}
            {locationTitle ? (
              <span className="day-main-event">
                {locationTitle.icon} {locationTitle.text}
              </span>
            ) : (
              <span className="day-no-events">
                {unplanned ? '📋 Not planned yet' : 'No scheduled events'}
              </span>
            )}
          </div>
        </div>
        <div className="day-header-right">
          {events.length > 0 && (
            <span className="event-count-badge">{events.length}</span>
          )}
          <span className="day-chevron">{open ? '▲' : '▼'}</span>
        </div>
      </button>

      {open && (
        <div className="day-body">
          {events.length > 0 ? (
            <div className="day-events">
              {events.map(event => (
                <EventDetails key={event.id} event={event} onViewDocument={onViewDocument} />
              ))}
            </div>
          ) : (
            <p className="day-empty">No travel events for this day.</p>
          )}

          <RecommendationsPanel
            tripId={tripId}
            date={date}
            existingRec={recommendation}
          />
        </div>
      )}
    </div>
  );
}

// ─── Document Upload ─────────────────────────────────────────
function DocumentUpload({ tripId, onUploaded }) {
  const [dragging, setDragging] = useState(false);
  const [uploads, setUploads] = useState([]);

  const handleFiles = async (files) => {
    const fileArray = Array.from(files);

    for (const file of fileArray) {
      const id = Date.now() + Math.random();
      setUploads(prev => [...prev, { id, name: file.name, progress: 0, status: 'uploading' }]);

      try {
        const result = await uploadDocument(tripId, file, (pct) => {
          setUploads(prev =>
            prev.map(u => u.id === id ? { ...u, progress: pct } : u)
          );
        });
        setUploads(prev =>
          prev.map(u => u.id === id ? { ...u, progress: 100, status: 'done' } : u)
        );
        onUploaded(result);
      } catch (err) {
        setUploads(prev =>
          prev.map(u => u.id === id ? { ...u, status: 'error', error: err.response?.data?.error } : u)
        );
      }
    }
  };

  // ── Google Drive Picker (manual GIS token flow) ──────────────
  // We do NOT rely on react-google-drive-picker's internal token exposure.
  // Instead: 1) request OAuth token with drive.file scope via GIS
  //          2) after token received, load Picker API and open it.
  const driveTokenRef = React.useRef(null);

  const loadScript = (src) =>
    new Promise((resolve, reject) => {
      if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
      const s = document.createElement('script');
      s.src = src;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });

  const openPickerWithToken = async (token) => {
    await loadScript('https://apis.google.com/js/api.js');
    await new Promise((resolve) => window.gapi.load('picker', resolve));

    const apiKey = process.env.REACT_APP_GOOGLE_API_KEY || '';
    const picker = new window.google.picker.PickerBuilder()
      .addView(
        new window.google.picker.DocsView()
          .setIncludeFolders(true)
          .setSelectFolderEnabled(false)
          .setMimeTypes('application/pdf,image/jpeg,image/png,image/webp,text/plain')
      )
      .setOAuthToken(token)
      .setDeveloperKey(apiKey)
      .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
      .setCallback(async (data) => {
        if (data[window.google.picker.Response.ACTION] === window.google.picker.Action.PICKED) {
          const docs = data[window.google.picker.Response.DOCUMENTS];
          setUploads(prev => [...prev, { id: 'drive-fetch', name: 'Downloading from Google Drive…', progress: 10, status: 'uploading' }]);

          const downloadedFiles = await Promise.all(docs.map(async doc => {
            try {
              const response = await fetch(
                `https://www.googleapis.com/drive/v3/files/${doc[window.google.picker.Document.ID]}?alt=media`,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              if (!response.ok) {
                console.error(`Drive download failed for ${doc[window.google.picker.Document.NAME]}:`, response.status);
                return null;
              }
              const blob = await response.blob();
              return new File([blob], doc[window.google.picker.Document.NAME], { type: blob.type || 'application/octet-stream' });
            } catch (err) {
              console.error('Drive DL error:', err);
              return null;
            }
          }));

          setUploads(prev => prev.filter(u => u.id !== 'drive-fetch'));
          const validFiles = downloadedFiles.filter(Boolean);
          if (validFiles.length > 0) handleFiles(validFiles);
        }
      })
      .build();
    picker.setVisible(true);
  };

  const handleOpenPicker = async (e) => {
    e.stopPropagation();
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
    if (!clientId) {
      alert('Google Client ID is not configured. Please set REACT_APP_GOOGLE_CLIENT_ID.');
      return;
    }

    // If we have a cached (non-expired) token, open picker immediately
    if (driveTokenRef.current) {
      openPickerWithToken(driveTokenRef.current);
      return;
    }

    // Load GIS and request a token with drive.file scope
    await loadScript('https://accounts.google.com/gsi/client');
    const tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: 'https://www.googleapis.com/auth/drive.readonly',
      callback: (tokenResponse) => {
        if (tokenResponse.error) {
          console.error('GIS token error:', tokenResponse);
          alert(`Google Drive auth failed: ${tokenResponse.error}`);
          return;
        }
        driveTokenRef.current = tokenResponse.access_token;
        // Token expires in ~1 hour — clear cache so we re-auth next time
        setTimeout(() => { driveTokenRef.current = null; }, (tokenResponse.expires_in - 60) * 1000);
        openPickerWithToken(tokenResponse.access_token);
      },
    });
    tokenClient.requestAccessToken({ prompt: 'consent' });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div className="doc-upload-section">
      <h3 className="section-subtitle">Upload Documents</h3>
      <div
        className={`drop-zone ${dragging ? 'dragging' : ''}`}
        onDragEnter={() => setDragging(true)}
        onDragLeave={() => setDragging(false)}
        onDragOver={e => e.preventDefault()}
        onDrop={handleDrop}
        onClick={() => document.getElementById('file-input').click()}
      >
        <input
          id="file-input"
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png,.webp,.txt"
          style={{ display: 'none' }}
          onChange={e => handleFiles(e.target.files)}
        />
        <div className="drop-zone-icon">📎</div>
        <p className="drop-zone-text">
          Drop files here or <span className="drop-zone-link">browse</span>
        </p>
        <p className="drop-zone-hint">PDF, JPG, PNG — up to 20MB each</p>
        <button type="button" className="btn-drive-upload" onClick={handleOpenPicker}>
          <img src="https://upload.wikimedia.org/wikipedia/commons/1/12/Google_Drive_icon_%282020%29.svg" alt="Google Drive" className="btn-drive-icon" />
          Add from Google Drive
        </button>
      </div>

      {uploads.length > 0 && (
        <div className="upload-list">
          {uploads.map(u => (
            <div key={u.id} className={`upload-item ${u.status}`}>
              <div className="upload-item-top">
                <span className="upload-filename">{u.name}</span>
                <span className="upload-status-icon">
                  {u.status === 'uploading' ? '⏳' : u.status === 'done' ? '✅' : '❌'}
                </span>
              </div>
              {u.status === 'uploading' && (
                <div className="upload-progress">
                  <div className="upload-progress-bar" style={{ width: `${u.progress}%` }} />
                </div>
              )}
              {u.status === 'error' && <p className="upload-error">{u.error || 'Upload failed'}</p>}
              {u.status === 'done' && <p className="upload-success">Processed and added to timeline</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Document List ───────────────────────────────────────────
function DocumentList({ tripId, documents, onDeleted, onView }) {
  const [deleting, setDeleting] = useState(null);

  const handleDelete = async (docId) => {
    if (!window.confirm('Remove this document?')) return;
    setDeleting(docId);
    try {
      await deleteDocument(tripId, docId);
      onDeleted(docId);
    } finally {
      setDeleting(null);
    }
  };

  if (documents.length === 0) {
    return <p className="docs-empty">No documents uploaded yet.</p>;
  }

  const typeIcon = { 'application/pdf': '📄', 'image/jpeg': '🖼️', 'image/png': '🖼️', 'image/webp': '🖼️', 'text/plain': '📝' };

  return (
    <div className="doc-list">
      {documents.map(doc => (
        <div key={doc.id} className="doc-item">
          <div className="doc-info" onClick={() => onView && onView({ id: doc.id, name: doc.original_name })} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span className="doc-icon">{typeIcon[doc.mime_type] || '📎'}</span>
            <span className="doc-name">{doc.original_name}</span>
            {doc.parsed_data?.type && (
              <span className="doc-type-badge">
                {EVENT_TYPE[doc.parsed_data.type]?.icon} {EVENT_TYPE[doc.parsed_data.type]?.label || doc.parsed_data.type}
              </span>
            )}
          </div>
          <button
            className="btn-doc-delete"
            onClick={() => handleDelete(doc.id)}
            disabled={deleting === doc.id}
          >
            {deleting === doc.id ? '...' : '×'}
          </button>
        </div>
      ))}
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────
export default function TripDetailPage() {
  const { id } = useParams();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMobileDocs, setShowMobileDocs] = useState(false);
  const today = format(new Date(), 'yyyy-MM-dd');

  const loadTrip = useCallback(async () => {
    try {
      const data = await getTrip(id);
      setTrip(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadTrip();
  }, [loadTrip]);

  const handleUploaded = ({ document: doc }) => {
    setTrip(prev => {
      const newDocs = [...(prev.documents || []), doc];
      // Reload full trip to get updated timeline
      loadTrip();
      return { ...prev, documents: newDocs };
    });
  };

  const handleDocDeleted = (docId) => {
    setTrip(prev => ({
      ...prev,
      documents: prev.documents.filter(d => d.id !== docId),
    }));
    // Reload to refresh timeline
    loadTrip();
  };

  const handleViewDocument = (docRef) => {
    const token = localStorage.getItem('token');
    const url = `/api/documents/${docRef.id}/view?token=${token}`;
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className="trip-detail-page">
        <Navbar />
        <div className="trip-detail-loading">
          <div className="loading-spinner" />
          <p>Loading trip...</p>
        </div>
      </div>
    );
  }

  if (!trip) {
    return (
      <div className="trip-detail-page">
        <Navbar />
        <div className="trip-not-found">Trip not found.</div>
      </div>
    );
  }

  // Build day-by-day structure
  const days = eachDayOfInterval({
    start: parseISO(trip.start_date),
    end: parseISO(trip.end_date),
  }).map(d => format(d, 'yyyy-MM-dd'));

  // Group and sort events by date and time
  const eventsByDate = {};
  (trip.timeline || []).forEach(event => {
    // Skip hotel check-out events so they don't appear in the timeline twice
    const label = (event.details?.label || '').toLowerCase();
    if (event.event_type === 'hotel' && (label === 'check_out' || label === 'checkout')) {
      return;
    }

    const d = format(parseISO(event.event_date), 'yyyy-MM-dd');
    if (!eventsByDate[d]) eventsByDate[d] = [];
    eventsByDate[d].push(event);
  });

  // Sort events chronologically within each day
  Object.keys(eventsByDate).forEach(date => {
    eventsByDate[date].sort((a, b) => {
      const timeA = (a.details && a.details.time) ? a.details.time : '23:59';
      const timeB = (b.details && b.details.time) ? b.details.time : '23:59';
      return timeA.localeCompare(timeB);
    });
  });

  // Group recommendations by date
  const recsByDate = {};
  (trip.recommendations || []).forEach(rec => {
    const d = format(parseISO(rec.event_date), 'yyyy-MM-dd');
    recsByDate[d] = rec;
  });

  const duration = days.length;

  // Pre-calculate booking coverages to know which inherited cities are "confirmed"
  const hotelIntervals = []; // { start, end, city }
  const carIntervals = [];   // { start, end }

  (trip.timeline || []).forEach(event => {
    const d = event.details || {};
    const eDate = parseISO(event.event_date);
    
    if (event.event_type === 'hotel') {
      const locs = d.locations || {};
      const city = locs.city || locs.city_to || d.hotel_name; // fallback name if no city
      let checkOutStr = d.checkout;
      
      // If no valid checkout, assume 1-day coverage
      if (!checkOutStr || isNaN(new Date(checkOutStr).getTime())) {
        hotelIntervals.push({ start: eDate, end: eDate, city });
      } else {
        const cOutDate = parseISO(checkOutStr);
        // Hotel checkout day is NOT covered for the night, so coverage is up to day BEFORE checkout
        // For title inheritance, we often say you are *in* that city until the flight leaves.
        // But strictly by hotel coverage, it covers the days you paid for minus the last checkout morning.
        hotelIntervals.push({ start: eDate, end: new Date(cOutDate.getTime() - 86400000), city });
      }
    } else if (event.event_type === 'car_rental') {
      // Car rentals covers start and end exactly based on parsing, fallback to just start date
      let checkoutStr = d.checkout || d.dropoff_time || null;
      if (!checkoutStr || isNaN(new Date(checkoutStr).getTime())) {
         carIntervals.push({ start: eDate, end: eDate });
      } else {
         carIntervals.push({ start: eDate, end: parseISO(checkoutStr) });
      }
    }
  });

  const isCoveredByIntervals = (dateStr, intervals) => {
    const dTime = parseISO(dateStr).getTime();
    return intervals.find(i => dTime >= i.start.getTime() && dTime <= i.end.getTime());
  };

  const dayTitles = {};
  const dayIsUnplanned = {};
  let lastKnownTitle = { text: trip.destination, icon: '📍', destCity: trip.destination };

  days.forEach(date => {
    const events = eventsByDate[date] || [];
    const derivedTitle = getDayLocationTitle(events, lastKnownTitle.destCity);

    if (derivedTitle) {
      lastKnownTitle = derivedTitle;
      // After a travel day, subsequent days should show just the destination city
      if (derivedTitle.destCity && (derivedTitle.icon === '✈️' || derivedTitle.icon === '🚗')) {
        lastKnownTitle = { text: derivedTitle.destCity, icon: '📍', destCity: derivedTitle.destCity };
        dayTitles[date] = derivedTitle; // The travel day itself gets the arrow/car
        dayIsUnplanned[date] = false;
        return;
      }
      dayTitles[date] = derivedTitle;
      dayIsUnplanned[date] = false;
      return;
    }

    // No flight/hotel check-in on this day. So we inherit `lastKnownTitle`.
    // But is it actually planned/covered?
    if (events.length > 0) {
      // Has some activities/other events. Definitely planned.
      dayTitles[date] = lastKnownTitle;
      dayIsUnplanned[date] = false;
    } else {
      // Empty day. Check if there is a hotel or car covering it
      const coveringHotel = isCoveredByIntervals(date, hotelIntervals);
      const coveringCar = isCoveredByIntervals(date, carIntervals);

      if (coveringHotel) {
        const hCity = coveringHotel.city || lastKnownTitle.destCity;
        lastKnownTitle = { text: hCity, icon: '📍', destCity: hCity };
        dayTitles[date] = lastKnownTitle; 
        dayIsUnplanned[date] = false;
      } else if (coveringCar) {
        dayTitles[date] = lastKnownTitle; 
        dayIsUnplanned[date] = false;
      } else {
        // Show as "Not planned" instead of automatically inheriting the city
        dayTitles[date] = null;
        dayIsUnplanned[date] = true;
      }
    }
  });

  return (
    <div className="trip-detail-page">
      <Navbar />

      {/* Hero */}
      <div className="trip-hero">
        <div className="trip-hero-inner">
          <div className="trip-hero-content">
            {trip.destination && (
              <div className="trip-hero-dest">
                <span>📍</span> {trip.destination}
              </div>
            )}
            <div className="trip-hero-title-row">
              <h1 className="trip-hero-name">{trip.name}</h1>
              <button className="mobile-docs-btn" onClick={() => setShowMobileDocs(true)}>
                📎 {(trip.documents || []).length}
              </button>
            </div>
            <div className="trip-hero-meta">
              <span>📅 {format(parseISO(trip.start_date), 'MMM d')} – {format(parseISO(trip.end_date), 'MMM d, yyyy')}</span>
              <span>·</span>
              <span>{duration} day{duration !== 1 ? 's' : ''}</span>
              <span>·</span>
              <span>{(trip.documents || []).length} document{(trip.documents || []).length !== 1 ? 's' : ''}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="trip-detail-body">
        <div className="trip-main">
          {/* Timeline */}
          <section className="timeline-section">
            <h2 className="section-title">
              📅 Trip Timeline
              <span className="section-count">{days.length} days</span>
            </h2>
            <div className="timeline-list">
              {days.map(date => (
                <DayAccordion
                  key={date}
                  date={date}
                  events={eventsByDate[date] || []}
                  recommendation={recsByDate[date] || null}
                  tripId={trip.id}
                  isToday={date === today}
                  locationTitle={dayTitles[date]}
                  unplanned={dayIsUnplanned[date]}
                  onViewDocument={handleViewDocument}
                />
              ))}
            </div>
          </section>
        </div>

        <aside className="trip-sidebar">
          {/* Upload */}
          <div className="sidebar-card">
            <DocumentUpload tripId={trip.id} onUploaded={handleUploaded} />
          </div>

          {/* Documents */}
          <div className="sidebar-card">
            <h3 className="section-subtitle">Documents ({(trip.documents || []).length})</h3>
            <DocumentList
              tripId={trip.id}
              documents={trip.documents || []}
              onDeleted={handleDocDeleted}
              onView={handleViewDocument}
            />
          </div>
        </aside>
      </div>

      {showMobileDocs && (
        <>
          <div className="mobile-drawer-overlay" onClick={() => setShowMobileDocs(false)} />
          <div className="mobile-drawer">
            <div className="mobile-drawer-header">
              <h3>Documents</h3>
              <button className="btn-close-drawer" onClick={() => setShowMobileDocs(false)}>×</button>
            </div>
            <div className="mobile-drawer-content">
              <DocumentUpload tripId={trip.id} onUploaded={handleUploaded} />
              <div className="drawer-docs-list">
                <h3 className="section-subtitle" style={{ marginTop: '16px' }}>Uploaded Files ({(trip.documents || []).length})</h3>
                <DocumentList
                  tripId={trip.id}
                  documents={trip.documents || []}
                  onDeleted={handleDocDeleted}
                  onView={handleViewDocument}
                />
              </div>
            </div>
          </div>
        </>
      )}


    </div>
  );
}
